import { Component, AfterViewInit, ViewChild, Inject } from "@angular/core";
import { FormGroup, FormControl } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { LxmMessage, LxmDialog } from "src/app/_helpers";
import { nextSchemeDay } from "src/app/_helpers/calculate";
import { SupplierProductAssortmentService } from "src/app/_services";
import { DialogComponent } from "src/app/components";
import { SupplierProductAssortmentDeliveryStatus, SupplierProductAssortmentStatus, asArray, asObject } from "src/app/enum";
import { IRetailChain } from "../../product-info/product-info.model";
import { ISupplierProductAssortment } from "../supplier-product-status.model";
import { IProductAssortmentDialogData } from "../supplier-product-status.card";

@Component({
  selector: "supplier-product-status-base-schema-dialog",
  host: { class: "dialog" },
  templateUrl: "./supplier-product-status-schema.dialog.html"
})
export class SupplierProductStatusBaseSchemaDialog
  implements AfterViewInit {
  @ViewChild("dialog", { static: true, read: DialogComponent })
  private _dialog: DialogComponent;

  private _enabledStatuses = [
    SupplierProductAssortmentStatus.Active,
    SupplierProductAssortmentStatus.EndOfLife,
    SupplierProductAssortmentStatus.Inactive
  ];

  public actionLoading = {} as any;

  public isNew = false;

  public statuses = this._enabledStatuses.map((key) =>
    asObject(SupplierProductAssortmentStatus, key)
  );
  public retailChains: IRetailChain[];

  public form: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IProductAssortmentDialogData,
    public dialogRef: MatDialogRef<SupplierProductStatusBaseSchemaDialog>,
    private _supplierProductAssortmentService: SupplierProductAssortmentService,
    private _message: LxmMessage,
    public dialog: LxmDialog,
    private _translateService: TranslateService
  ) {
    this.retailChains = data.retailChains
      .filter((x) => !x.isClient)
      .map((x) => Object.assign({}, x));

    this.form = new FormGroup({
      id: new FormControl(),
      validFrom: new FormControl(),
      status: new FormControl(SupplierProductAssortmentStatus.Active),
      retailersListIsExclusive: new FormControl(),
      retailers: new FormControl(),
      // isOrderableProduct: new FormControl(),
      // orderableWithPriceSchemeOnly: new FormControl(),
      // orderableRetailers: new FormControl(),
      // priceLists: new FormControl()
    });

    this.form.patchValue(this.normalizeData(this.data.schema));
  }

  // public get priceListOptions() {
  //   return this.data.assortmentCard.formData.priceLists;
  // }

  private normalizeData(data: ISupplierProductAssortment): any {
    this.isNew = true;
    const existingAssortment = this.data.assortmentCard.assortments?.[0];

    let result: any;

    if (data) {
      result = { ...data };
      this.isNew = false;
    } else if (existingAssortment) {
      result = { ...existingAssortment };
      result.id = null;
      result.validFrom = null;
    } else {
      result = <any>{};
    }

    result.retailers = result.retailerIds || result.retailers;

    result.validFrom = result.validFrom
      ? result.validFrom.editableDate
      : nextSchemeDay(existingAssortment?.validFrom.editableDate);

    return result;
  }

  ngAfterViewInit(): void {
    this._dialog.dataLoaded$.subscribe((state) => {
      if (!state) {
        return;
      }

      if (!this.data.assortmentCard.assortments.length) {
        this.form.patchValue({
          retailersListIsExclusive: this.data.formData.retailersListIsExclusive
        });
      }
    });
  }

  public save(): void {
    const f = this.form.value;

    const req = {
      productId: this.data.assortmentCard.productId,
      validFrom: f.validFrom,
      status: f.status,
      retailersListIsExclusive: f.retailersListIsExclusive || false,
      retailerIds: f.retailers
    };

    this._supplierProductAssortmentService
      .save(this.data.assortmentCard.productId, f.id, req)
      .result(
        this.form,
        () => {
          this.data.assortmentCard.loadBaseAssortments();
          this.closeDialog();
        },
        (err) => {
          this._message.error({
            message: err.validationSummary || "common.error.save_failed"
          });
        }, 'save', this.actionLoading);
  }

  public deleteScheme() {
    this.dialog.confirm(
      {
        image: "delete-modal-image",
        template: `
      <p class="dialog-title">${this._translateService.instant("cards.products.product_status.delete.title")}</p>
      <p>
        ${this._translateService.instant("cards.products.product_status.delete.message")}
      </p>
      `
      },
      () => {
        const id = this.data.schema.id;
        this._supplierProductAssortmentService
          .delete(this.data.assortmentCard.productId, id)
          .result(
            this.form,
            () => {
              this.data.assortmentCard.loadBaseAssortments();
              this.closeDialog();
            },
            () => {
            }, 'delete', this.actionLoading);
      }
    );
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public closeDialog() {
    this.dialogRef.close();
  }
}

@Component({
  selector: "supplier-product-assortment-delivery-status-dialog",
  host: { 'class': 'dialog' },
  templateUrl: "./supplier-product-assortment-delivery-status.dialog.html"
})
export class SupplierProductAssortmentDeliveryStatusDialog {

  @ViewChild('dialog', { static: true, read: DialogComponent })
  private _dialog: DialogComponent;

  public saveLoading = false;
  public statuses = asArray(SupplierProductAssortmentDeliveryStatus);

  public form: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IProductAssortmentDialogData,
    public dialogRef: MatDialogRef<SupplierProductAssortmentDeliveryStatusDialog>,
    private _supplierProductAssortmentService: SupplierProductAssortmentService,
    private _message: LxmMessage,
    public dialog: LxmDialog) {

    this.form = new FormGroup({
      id: new FormControl(data.schema.id),
      status: new FormControl(data.schema.deliveryStatus)
    });
  }

  public save(): void {
    this.saveLoading = true;
    const f = this.form.value;

    this._supplierProductAssortmentService
      .updateDeliveryStatus(this.data.assortmentCard.productId, f.id, f.status)
      .result(this.form, () => {
        this.data.assortmentCard.loadBaseAssortments();
        this.closeDialog();
      }, (err) => {

        this._message.error({
          message: err.validationSummary || 'common.error.save_failed',
        });

        this.saveLoading = false;
      });
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public closeDialog() {
    this.dialogRef.close();
  }
}